<script setup>
import { nextTick, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { RouterView, onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import EventChat from '../chat/EventChat.vue';
import SessionDialog from '../../livekit-client/components/SessionDialog.vue';
import XlButton from '../xl/XlButton.vue';
import { useAuthStore } from '../../stores/auth';
import { useSessionStore } from '../../stores/sessionStore';
import { useSocketStore } from '../../stores/socketStore';
import { mySessionRow } from '../../api/portal';
import { viewerTimeZone } from '../../helpers/greeting';
import { joinFromAgenda } from '../../helpers/sessionJoin';
import { reportError } from '../../faro';
import '../../chat.scss';

/**
 * The live room, hosted around every Sessions page (XN-4558) so Join opens the
 * same room from the list or a session's page, one leave guard covers both,
 * and moving between them keeps the call. It holds what the room needs to
 * run: the chat socket before EventChat mounts (it defines
 * window.joinSessionEvent), the presence heartbeat, a warning before leaving a
 * live call, and `?session=` for a link that arrives to open a room (reminder
 * emails link to /hub?session=, which redirects here).
 */
const router = useRouter();
const route = useRoute();
const auth = useAuthStore();
const sessionStore = useSessionStore();
const socketStore = useSocketStore();

const ready = ref(false);
const leaveWarning = ref(false);
let pendingLeave = null;
let leaving = false;
let heartbeat = null;
// Leaving while the socket connects unmounts the page first; what follows the
// await must then start nothing it can no longer clean up.
let disposed = false;

// Settles once EventChat has mounted, so a Join pressed while the socket is
// still connecting waits for the room instead of finding no joinSessionEvent.
let markReady;
const roomReady = new Promise((resolve) => { markReady = resolve; });

// Fires only when leaving /hub/sessions altogether: moving between its pages
// stays inside this route record, so the call carries on.
onBeforeRouteLeave((to) => {
  if (leaving || !sessionStore.isConnected) return true;
  pendingLeave = to;
  leaveWarning.value = true;
  return false;
});

// The router undid a Back or Forward when the guard said no. Replaying it as a
// push would add an entry that Back returns straight here from, so step through
// history instead. A nav click to the page behind this one steps back too, which
// lands in the same place.
const confirmLeave = () => {
  leaveWarning.value = false;
  leaving = true;
  const target = router.resolve(pendingLeave ?? '/hub').fullPath;
  const { back, forward } = router.options.history.state;
  if (target === back) router.back();
  else if (target === forward) router.forward();
  else router.push(target);
};

const cancelLeave = () => {
  leaveWarning.value = false;
  pendingLeave = null;
};

// Closing the tab or reloading, which the router never sees.
const onBeforeUnload = (event) => {
  if (!sessionStore.isConnected) return undefined;
  event.preventDefault();
  event.returnValue = 'Are you sure you want to leave? You will be disconnected from the call.';
  return event.returnValue;
};

/**
 * Open the room for one session. The row is the attendee feed's, fetched by
 * id, because joinSessionEvent wants that whole payload. False when the
 * session is gone, unpublished, not this person's to open, or this page has gone.
 */
const open = async (sessionId) => {
  await roomReady;
  if (disposed) return false;
  const target = await mySessionRow(sessionId, viewerTimeZone());
  if (disposed || !target || typeof window.joinSessionEvent !== 'function') return false;
  await joinFromAgenda(target);
  return true;
};

// What a Join button calls: the same open, saying so when it can't. One at a
// time: a second press while the first is pending gets the same promise.
let joining = null;
const join = (sessionId) => {
  joining ??= (async () => {
    try {
      if (!(await open(sessionId)) && !disposed) {
        window.loadNotification?.({ message: 'That session is no longer available.', error: true });
      }
    } catch (error) {
      reportError(error);
      window.loadNotification?.({ message: "The call couldn't be opened. Try again.", error: true });
    } finally {
      joining = null;
    }
  })();
  return joining;
};

provide('portalRoom', { join });

// A link that arrived to open a room. Quiet when it can't: the page still works.
const openSessionFromQuery = async () => {
  const sessionId = Number(route.query.session);
  if (!sessionId) return;

  try {
    await open(sessionId);
  } catch (e) {
    // Only the shortcut failed.
  } finally {
    // useRoute() is the router's current route, which after leaving is the
    // next page's: rewriting it would drop that page's own query.
    if (!disposed) {
      const { session, ...rest } = route.query;
      router.replace({ path: route.path, query: rest });
    }
  }
};

onMounted(async () => {
  try {
    await socketStore.connect();
  } finally {
    ready.value = true;
  }
  await nextTick();
  if (disposed) return;
  markReady();
  openSessionFromQuery();

  heartbeat = setInterval(() => {
    const user = auth.userData;
    if (user?.id && socketStore.socket?.connected) {
      socketStore.socket.emit('heartbeat', { domain: window.store.subdomain, userId: user.id, timestamp: Date.now() });
    }
  }, 30000);

  window.addEventListener('beforeunload', onBeforeUnload);
});

onBeforeUnmount(() => {
  disposed = true;
  markReady();
  clearInterval(heartbeat);
  window.removeEventListener('beforeunload', onBeforeUnload);
});
</script>

<template>
  <SessionDialog v-if="leaveWarning" title="You're about to leave the call" icon="leave" data-testid="leave-call-dialog">
    <p>Are you sure you want to leave the session? You will be disconnected from the call.</p>
    <template #actions>
      <XlButton variant="ghost" @click="cancelLeave">Cancel</XlButton>
      <XlButton data-testid="leave-call-confirm" @click="confirmLeave">Leave</XlButton>
    </template>
  </SessionDialog>

  <RouterView />

  <div v-if="ready" id="omnichat-event" class="box-event">
    <EventChat hide-header />
  </div>
</template>
